import { ERROR_CODE } from '@/lib/config/errorCode';
import { SERVER_CODE } from '@/lib/config/serverCode';
import { api, ResponseMap, staticDataApi } from '@/lib/utils/api.utils';
import { Language } from '@/i18n/settings';
import {
  useQuery,
  UseQueryResult,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { toaster } from '@/components/ui/toaster';
import { getRequestLanguage } from '@/components/common/ClientProvider';
import { useSession } from '../hooks/useSession';
const requestUrl = process.env.NEXT_PUBLIC_REQUEST_URL;

export enum AuditStatus {
  UNKNOWN,
  PASSED,
  DENIED,
}

export type TokenInfo = {
  baseTokenPrice: number;
  baseTokenSymbol: string;
  buyTax: number;
  chainIcon: string;
  chainId: string;
  change24: number;
  change1m: number;
  change1h: number;
  change5m: number;
  createdAt: string;
  currentBaseTokenAmount: number;
  currentTokenAmount: number;
  decimals: string;
  dexName: string;
  fdv: number;

  domesticProgress: number;
  /**
   * 交易是否可被冻结, 0:没有数据 1:不可冻结 2:可以冻结
   */
  freezable: AuditStatus;
  holdCount: string;
  initBaseTokenAmount: number;
  initTokenAmount: number;
  isCheckCa: boolean;
  isFollowed: boolean;
  liquidity: number;
  /**
   * 是否可锁仓, 0:没有数据 1:不可 2:可以锁仓
   */
  lockLiquidity: AuditStatus;
  /**
   * 是否可增发token, 0:没有数据 1:不可mint 2:可以mint
   */
  mintable: AuditStatus;
  mktCap: number;
  pairAddress: string;
  sellTax: number;
  telegram: string;
  tokenAddress: string;
  tokenIcon: string;
  tokenPrice: number;
  tokenSymbol: string;
  topHolder: number;
  totalSupply: number;
  twitterUsername: string;
  txs24h: number;
  vol24h: number;
  website: string;
};

export async function requestTokenInfoByTokenAddress(
  chainId: number,
  tokenAddress: string,
): Promise<TokenInfo | undefined> {
  const locale = getRequestLanguage();
  try {
    const response = await api.get(`${requestUrl}/v1/market/get_token_info`, {
      searchParams: {
        chain_id: chainId,
        token_address: tokenAddress,
      },
    });
    if (!response.ok) {
      throw new Error(ERROR_CODE.NETWORK_ERROR);
    }
    const data = await response.json<ResponseMap<TokenInfo>>();
    if (data.code !== SERVER_CODE.SUCCESS) {
      throw new Error(data.message);
    }
    return data.data;
  } catch (e: any) {
    // toaster.create({
    //   title: e.message,
    //   type: 'error',
    // });
  }
}

export type MarketTrending = {
  chainIcon: string;
  chainId: number;
  chainName: string;
  change1h: number;
  change24h: number;
  change4h: number;
  change5m: number;
  id: number;
  isFollowed: boolean;
  liq: string;
  mcap: string;
  pond: string;
  price: string;
  safetyCheck: null;
  tokenAddress: string;
  pairAddress: string;
  tokenIcon: string;
  tokenName: string;
};

export interface MarketTrendingData {
  list: Array<MarketTrending>;
  token?: MarketTrending;
}

export async function requestMarketSearchTrending(
  locale: Language,
  chainId: number,
): Promise<MarketTrendingData> {
  const response = await api.get(`${requestUrl}/v1/market/trending_search`, {
    searchParams: {
      chain_id: chainId,
    },
  });
  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }
  const data = await response.json<ResponseMap<MarketTrendingData>>();
  if (!data.data) {
    throw new Error(ERROR_CODE.EMPTY_DATA);
  }
  return data.data;
}

export async function getMarketTrending(
  locale: Language,
  chainId: number,
): Promise<MarketTrendingData | null> {
  try {
    return await requestMarketSearchTrending(locale, chainId);
  } catch (e) {
    return null;
  }
}

/**
 * 收藏类型, 1:收藏 2:取消收藏
 */
export type FavoriteType = 1 | 2;
export type TokenFavorite = {
  pair_address: string;
  favorite_type: FavoriteType;
  chain_id: number;
};

export async function postMarketTokenFavorite(
  params: TokenFavorite,
): Promise<boolean | undefined> {
  try {
    const response = await api.post(`${requestUrl}/v1/market/token_favorite`, {
      json: params,
    });
    if (!response.ok) {
      throw new Error(ERROR_CODE.NETWORK_ERROR);
    }
    const data = await response.json<ResponseMap<boolean>>();
    if (data.data === undefined) {
      throw new Error(ERROR_CODE.EMPTY_DATA);
    }
    if (data.code != SERVER_CODE.SUCCESS) {
      throw new Error(data.message);
    }
    return data.data;
  } catch (e: any) {
    toaster.create({
      title: e.message,
      type: 'error',
    });
    throw e;
  }
}

export async function requestMarketSearch(
  keyword: string,
): Promise<MarketTrendingData> {
  const response = await api.get(`${requestUrl}/v1/market/search`, {
    searchParams: {
      keyword: keyword,
    },
  });
  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }
  const data = await response.json<ResponseMap<MarketTrendingData>>();
  if (!data.data) {
    throw new Error(ERROR_CODE.EMPTY_DATA);
  }
  return data.data;
}

export interface QueryHolderListPayload {
  pageNo: number;
  pageSize: number;
  tokenCa: string;
  chainId: number;
}

export interface HolderListItem {
  balance: string;
  ownerAddress: string;
  proportion: number;
}

export interface HolderListResponse {
  list: HolderListItem[];
  total: number;
}

export async function requestHolderList(
  params: QueryHolderListPayload,
): Promise<HolderListResponse | undefined> {
  const response = await api.get(`${requestUrl}/v1/market/token_holder_list`, {
    searchParams: {
      page_no: params.pageNo,
      page_size: params.pageSize,
      token_address: params.tokenCa,
      chain_id: params.chainId,
    },
    timeout: 20000,
  });

  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }

  const data = await response.json<ResponseMap<HolderListResponse>>();
  if (data.code !== SERVER_CODE.SUCCESS) {
    throw new Error(data.message);
  }

  return data.data;
}

export function useInfiniteHolderList(
  params: Omit<QueryHolderListPayload, 'pageNo'>,
) {
  return useInfiniteQuery({
    queryKey: ['infinite_holder_list', params],
    queryFn: ({ pageParam = 1 }) =>
      requestHolderList({
        ...params,
        pageNo: pageParam,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.list.length < params.pageSize) {
        return undefined;
      }
      return allPages.length + 1;
    },
    initialPageParam: 1,
  });
}

export interface QueryTradeListPayload {
  pageNo: number;
  pageSize: number;
  pairAddress: string;
  chainId: string;
  fixed?: boolean;
  start?: number;
  end?: number;
}

export interface TradeInfo {
  baseTokenAmount: number;
  baseTokenPriceUsd: number;
  blockNum: string;
  blockTimestamp: string;
  clampType: string;
  maker: string;
  pairAddress: string;
  sort: string;
  swapName: string;
  to: string;
  tokenAmount: number;
  tokenPriceUsd: number;
  totalUsd: number;
  tradeType: string;
  txHash: string;
}

export interface TradeListResponse {
  list: TradeInfo[];
  total: number;
}

export async function requestTradeList(
  params: QueryTradeListPayload,
): Promise<TradeListResponse> {
  const locale = getRequestLanguage();
  const searchParams: Record<string, string> = {
    page_no: params.pageNo.toString(),
    page_size: params.pageSize.toString(),
    pair_address: params.pairAddress,
    chain_id: params.chainId,
  };

  if (params.fixed !== undefined) {
    searchParams.fixed = params.fixed.toString();
  }
  if (params.start !== undefined) {
    searchParams.start = params.start.toString();
  }
  if (params.end !== undefined) {
    searchParams.end = params.end.toString();
  }

  const response = await staticDataApi(locale).get(
    `${requestUrl}/v1/market/get_trade_list`,
    {
      searchParams,
      timeout: 20000,
    },
  );

  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }

  const data = await response.json<ResponseMap<TradeListResponse>>();
  if (!data.data) {
    throw new Error(ERROR_CODE.EMPTY_DATA);
  }

  return data.data;
}

export function useInfiniteTradeList(
  params: Omit<QueryTradeListPayload, 'pageNo'>,
) {
  return useInfiniteQuery({
    queryKey: ['infinite_trade_list', params],
    queryFn: ({ pageParam = 1 }) =>
      requestTradeList({
        ...params,
        pageNo: pageParam,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.list.length < params.pageSize) {
        return undefined;
      }
      return allPages.length + 1;
    },
    initialPageParam: 1,
    refetchInterval: 10000, // 每10秒刷新一次
    enabled: !!(params.pairAddress && params.chainId),
  });
}

export function useTradeList(
  params: Omit<QueryTradeListPayload, 'pageNo' | 'pageSize'> & {
    pageSize?: number;
  },
): UseQueryResult<TradeListResponse, Error> {
  return useQuery({
    queryKey: ['trade_list', params],
    queryFn: () =>
      requestTradeList({
        ...params,
        pageNo: 1,
        pageSize: params.pageSize ?? 150,
      }),
    enabled: !!(params.pairAddress && params.chainId),
  });
}

export interface TopHolder {
  top10: number;
}

export interface TopHolderQuery {
  token_address: string;
  chain_id: number;
}

export async function requestTopHolder(
  params: TopHolderQuery,
): Promise<TopHolder | undefined> {
  const response = await api.get(`${requestUrl}/v1/market/get_top10`, {
    searchParams: {
      token_address: params.token_address,
      chain_id: params.chain_id,
    },
    timeout: 20000,
  });

  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }

  const data = await response.json<ResponseMap<TopHolder>>();
  if (data.code !== SERVER_CODE.SUCCESS) {
    throw new Error(data.message);
  }

  return data.data;
}
